import { dispatch as d3dispatch, json as d3json, select } from "d3c";
import { feature as topojsonFeature } from "topojson-client";
import d3wl from "utils/d3-wl-util";
import { each } from "lodash";

const dispatch = d3dispatch("mouseover", "mousemove", "mouseout", "click");

export default class GeomapCountries {
	constructor (options) {
		this.options = options;
		this._state = {
			zoomScale: 1
		};
		this.svgContainer = select(this.options.element).select("svg");
		this.layer = this.svgContainer
			.select(".geomap_zoomer")
			.append("g")
			.attr("class", "datamaps-subunits");
	}

	// returns promise
	init () {
		d3wl.rebind(this, dispatch, "on");

		// if custom URL for topojson data, retrieve it and render
		if (this.options.countriesConfig.url) {
			d3json(this.options.countriesConfig.url).then(json => {
				this.drawSubunits(json);
				this.addSubunitsEventListeners(this);
				this.svgContainer
					.selectAll(".datamaps-subunit.hover")
					.style("stroke-width", this.options.countriesConfig.highlightBorderWidth / this._state.zoomScale);
			});
		}
	}

	zoomed (mapState, currentZoomTransform, isZoomChanged) {
		this._state.zoomScale = currentZoomTransform.k;
		if (!this._state.path) {
			// Map was not initially zoomed yet
			this._state.path = mapState.path;
			this.updateSubunits();
		}
		if (isZoomChanged && this.options.countriesConfig.highlightOnHover) {
			this.svgContainer
				.selectAll(".datamaps-subunit.hover")
				.style("stroke-width", this.options.countriesConfig.highlightBorderWidth / currentZoomTransform.k);
		}
	}

	/**
	 * Get shape information from coordinates
	 * @param lat number latitude
	 * @param lng number longitude
	 * @return object <subunit-data>
	 * information about a shape, null if not in a shape
	 */
	getShape (lat, lng) {
		if (typeof lat !== "number" || typeof lng !== "number") {
			return new Error(`Wrong coordinates (${lat},${lng})`);
		}

		const subunits = this.svgContainer.selectAll(".datamaps-subunit").data();
		if (subunits.length < 1) {
			return null;
		}
		const point = [lng, lat];
		// Search country is often on last index or near it
		let i = subunits.length - 1;
		let found = false;

		do {
			const country = subunits[i];
			if (country.geometry.type === "Polygon") {
				found = polygonContainsPoint(country.geometry.coordinates[0], point);
			} else {
				// Multi Polygon
				let j = 0;
				do {
					found = polygonContainsPoint(country.geometry.coordinates[j][0], point);
					j++;
				} while (j < country.geometry.coordinates.length && !found);
			}

			i--;
		} while (i >= 0 && !found);

		return found ? subunits[i + 1] : null;
	}

	/**
	 * @param countryShape d3 selection
	 */
	highlightCountryBorder (countryShape) {
		countryShape
			.classed("hover", true)
			.style("fill", this.options.countriesConfig.highlightFillColor)
			.style("stroke", this.options.countriesConfig.highlightBorderColor)
			.style("stroke-width", this.options.countriesConfig.highlightBorderWidth / this._state.zoomScale)
			.style("fill-opacity", this.options.countriesConfig.highlightFillOpacity);
		if (!/MSIE/.test(navigator.userAgent) && countryShape.size() > 0) {
			moveToFront(countryShape.nodes()[0]);
		}
	}

	dehighlightCountryBorders () {
		this.svgContainer
			.selectAll(".datamaps-subunit")
			.classed("hover", false)
			.style("stroke-width", 0)
			.style("fill-opacity", 0);
	}

	updateSubunits () {
		this.svgContainer
			.select("g.datamaps-subunits")
			.selectAll("path.datamaps-subunit")
			.attr("d", this._state.path);
	}

	// Draw country shapes layer
	drawSubunits (data) {
		const geoConfig = this.options.countriesConfig || {};
		const subunits = this.svgContainer.select("g.datamaps-subunits");
		let geoData = topojsonFeature(data, data.objects[this.options.countriesConfig.scope]).features;

		if (geoConfig.hideAntarctica) {
			geoData = geoData.filter(function (feature) {
				return feature.id !== "ATA";
			});
		}

		const geo = subunits
			.selectAll("path.datamaps-subunit")
			.data(geoData, function (d) {
				if (d.id == null) {
					// purposely using loose equality here
					console.groupCollapsed("No id found for geometry feature");
					console.warn("“If a geometry has a commonly used identifier, that identifier should be included as a member of the geometry object with the name ‘id’”");
					console.log("https://github.com/topojson/topojson-specification/blob/master/README.md#22-geometry-objects");
					console.log(d);
					console.groupEnd();
				}
				return d.id;
			});

		geo
			.enter()
			.append("path")
			.attr("class", function (d) {
				return `datamaps-subunit ${d.id}`;
			})
			.merge(geo)
			.attr("d", this._state.path)
			.style("fill-opacity", 0)
			.style("stroke-width", 0);
	}

	addSubunitsEventListeners () {
		// to determine which labels to display we need the country shapes dimension bounds
		const subunits = this.svgContainer.selectAll(".datamaps-subunit");
		subunits
			.on("mouseover", function (d) {
				dispatch.call("mouseover", this, d);
			})
			.on("mouseout", function (d) {
				dispatch.call("mouseout", this, d);
			})
			.on("click", function (d) {
				dispatch.call("click", this, d);
			});
	}
}

/**
 * Checks if a point is contained in a polygon
 * (based on the Jordan curve theorem), for more info:
 * http://www.ecse.rpi.edu/Homepages/wrf/Research/Short_Notes/pnpoly.html
 * @param polygon array a series of the polygon's coordinates
 * @param point object representing the point's coordinates
 * @return boolean true if the point lies within the polygon, false otherwise
 */
const polygonContainsPoint = (polygon, point) => {
	const nvert = polygon.length;
	let c = false;

	for (let i = 0, j = nvert - 1; i < nvert; j = i++) {
		if (
			polygon[i][1] > point[1] !== polygon[j][1] > point[1] &&
			point[0] < ((polygon[j][0] - polygon[i][0]) * (point[1] - polygon[i][1])) / (polygon[j][1] - polygon[i][1]) + polygon[i][0]
		) {
			c = !c;
		}
	}
	return c;
};

// Workaround for IE compability
// as per discussion on https://github.com/markmarkoh/datamaps/issues/19
const moveToFront = node => {
	const mover = function () {
		node.parentNode.appendChild(node);
	};

	if (!Array.isArray(node)) {
		return mover.call(node);
	}

	return each(node, mover);
};
